import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../../GlobalFunction/userPermissions';
import {
    Box,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions, CircularProgress,
    Button,
    Typography, Checkbox, FormControlLabel, FormGroup, Alert
} from '@mui/material';
import FilterTable from '../../../../atoms/TableComponent/FilterTable/FilterTable';
import axiosInstance from '../../../../lib/axios';

export default function YojanamstTable() {
    const [isLoading, setIsLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const navigate = useNavigate();
    const permissions = usePermissions("Yojana Master");
    const { AddRec = "0" } = permissions || {};
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [openPopup, setOpenPopup] = useState(false);
    const [selectedCellInfo, setSelectedCellInfo] = useState(null);
    const [yojanaEntities, setYojanaEntities] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [apiError, setApiError] = useState(null);

    const lastClickTimeRef = useRef(0);
    const isDoubleClickRef = useRef(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchYojanaDetails = async (yojanaId) => {
        setLoadingDetails(true);
        setApiError(null);
        try {
            const response = await axiosInstance.post(
                'MstYojana/RetriveMstYojanaAll',
                {
                    "YojanaId": yojanaId,
                    "Flag": "R"
                }
            );

            if (response.data.Status === 0 && response.data.Data?.MstYojanaDtlEntities?.length > 0) {
                const entities = response.data.Data.MstYojanaDtlEntities[0]?.MstYojanaEntities || [];
                setYojanaEntities(entities);
            } else {
                setYojanaEntities([]);
                setApiError("No task details found for this Yojana");
            }
        } catch (error) {
            console.error('Error while fetching Yojana details:', error);
            setApiError("Failed to load task details. Please try again.");
            setYojanaEntities([]);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleCellClicked = (params) => {
        const currentTime = new Date().getTime();
        const timeDiff = currentTime - lastClickTimeRef.current;
        if (timeDiff < 300) {
            isDoubleClickRef.current = true;
            return;
        }

        lastClickTimeRef.current = currentTime;
        isDoubleClickRef.current = false;

        setTimeout(() => {
            if (!isDoubleClickRef.current) {
                const clickableColumns = ['YojanaName', 'Abrv', 'Remark'];

                if (clickableColumns.includes(params.colDef.field)) {
                    console.log('Single cell clicked:', params);
                    setSelectedRowData(params.data);
                    setSelectedCellInfo({
                        field: params.colDef.field,
                        value: params.value,
                        columnName: params.colDef.headerName
                    });

                    setYojanaEntities([]);
                    fetchYojanaDetails(params.data.YojanaId);
                    setOpenPopup(true);
                }
            }
        }, 300);
    };

    const handleRowDoubleClicked = (params) => {
        isDoubleClickRef.current = true;

        const yojanaId = params?.data?.YojanaId;
        if (yojanaId) {
            navigate('/yojana-mst', { state: { YojanaId: yojanaId, mode: 'view' } });
        }
    };

    const handleClosePopup = () => {
        setOpenPopup(false);
        setSelectedRowData(null);
        setSelectedCellInfo(null);
    };

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.post(
                'MstYojana/GetMstYojanaDashBoard',
                {
                    start: 1,
                    PageSize: 1000,
                    SearchText: ""
                }
            );
            if (response.data.Status === 0) {
                const formattedData = response.data.Data.map((item, index) => ({
                    ...item,
                    RowNum: index + 1,
                    YojanaName: item.YojanaName || '',
                    Abrv: item.Abrv || '',
                    Remark: item.Remark || '',
                    Status: item.Status == null ? '' : (item.Status == 1 ? 'Active' : 'Inactive'),
                    YojanaId: item.YojanaId
                }));
                setRows(formattedData);
            } else {
                setRows([]);
            }
        } catch (error) {
            console.error('Error while fetching data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePermittedClick = () => {
        if (AddRec === "1") {
            navigate('/yojana-mst', { state: { mode: "add" } });
        }
    };

    const handleHomeClick = () => {
        navigate('/dashboard');
    };

    const handleLocationclick = () => {
        navigate('/masters/operations');
    };


    const createCellRenderer = (isClickable = false) => {
        const CellRenderer = (props) => {
            const [isOverflowed, setIsOverflowed] = useState(false);
            const cellRef = React.useRef(null);

            useEffect(() => {
                if (cellRef.current) {
                    const isTextOverflowed =
                        cellRef.current.scrollWidth > cellRef.current.clientWidth ||
                        (props.value && props.value.length > 20);
                    setIsOverflowed(isTextOverflowed);
                }
            }, [props.value]);

            const cellValue = props.valueFormatted ? props.valueFormatted : props.value;

            const handleClick = (e) => {

                if (e.detail === 1 && isClickable) {

                }
            };

            return (
                <div
                    ref={cellRef}
                    className="truncated-cell"
                    style={{
                        width: '100%',
                        cursor: isClickable ? 'pointer' : 'default',
                        padding: '5px 0',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                    title={isOverflowed ? cellValue : ''}
                    onClick={handleClick}
                >
                    {cellValue}
                </div>
            );
        };
        return CellRenderer;
    };

    const StatusCellRenderer = (props) => {
        const getStatusColor = (status) => {
            switch (status) {
                case 'Active':
                    return '#4caf50';
                case 'Inactive':
                    return '#f44336';
                default:
                    return '#f44336';
            }
        };

        return (
            <Chip
                label={props.value}
                className="status-chip"
                sx={{
                    backgroundColor: getStatusColor(props.value),
                    color: 'white',
                    width: '100%',
                    maxWidth: '100px',
                }}
            />
        );
    };

    const columnDefs = [
        {
            headerName: 'Sr.No.',
            field: 'RowNum',
            width: 80,
            filter: 'agTextColumnFilter',
            cellRenderer: createCellRenderer(false),
            sortable: true,
            resizable: true,
            cellStyle: { cursor: 'default' }
        },
        {
            headerName: 'Yojana Name',
            field: 'YojanaName',
            minWidth: 250,
            flex: 1,
            filter: 'agTextColumnFilter',
            cellRenderer: createCellRenderer(true),
            sortable: true,
            resizable: true,
            cellStyle: { cursor: 'pointer' }
        },
        {
            headerName: 'Abbreviation',
            field: 'Abrv',
            minWidth: 150,
            flex: 1,
            filter: 'agTextColumnFilter',
            cellRenderer: createCellRenderer(true),
            sortable: true,
            resizable: true,
            cellStyle: { cursor: 'pointer' }
        },
        {
            headerName: 'Remark',
            field: 'Remark',
            minWidth: 250,
            flex: 1,
            filter: 'agTextColumnFilter',
            cellRenderer: createCellRenderer(true),
            sortable: true,
            resizable: true,
            cellStyle: { cursor: 'pointer' }
        },
        {
            headerName: 'Status',
            field: 'Status',
            width: 120,
            filter: 'agTextColumnFilter',
            cellRenderer: StatusCellRenderer,
            sortable: true,
            resizable: true,
            cellStyle: { cursor: 'default' }
        },
    ];

    return (
        <Box sx={{ height: '90vh' }}>
            <FilterTable
                columnDefs={columnDefs}
                rowData={rows}
                onRowDoubleClicked={handleRowDoubleClicked}
                onCellClicked={handleCellClicked}
                isLoading={isLoading}
                onHomeClick={handleHomeClick}
                onParentClick={handleLocationclick}
                breadcrumbParentLabel="Operations"
                breadcrumbCurrentLabel="Yojana Master"
                onAddClick={handlePermittedClick}
                hasAddPermission={AddRec === "1"}
                suppressRowClickSelection={true}
            />

            <Dialog
                open={openPopup}
                onClose={handleClosePopup}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{
                    backgroundColor: '#635bff',
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding:'10px'
                }}>
                    <span>Yojana Details</span>
                    <Button
                        onClick={handleClosePopup}
                        sx={{ color: 'white', minWidth: 'auto' }}
                    >
                        ✕
                    </Button>
                </DialogTitle>

                <DialogContent sx={{ mt: 1 }}>
                    {selectedRowData && selectedCellInfo && (
                        <Box sx={{ p: 0 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, mb: 1 }}>
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: '#666',fontSize:'15px',fontWeight:'bold' }}>
                                        Yojana Name
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedRowData.YojanaName}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: '#666',fontSize:'15px',fontWeight:'bold' }}>
                                        Status
                                    </Typography>
                                    <Chip
                                        label={selectedRowData.Status}
                                        size="small"
                                        sx={{
                                            backgroundColor: selectedRowData.Status === 'Active' ? '#4caf50' : '#f44336',
                                            color: 'white',
                                            fontWeight: 'bold'
                                        }}
                                    />
                                </Box>


                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: '#666',fontSize:'15px',fontWeight:'bold' }}>
                                    Remark
                                </Typography>
                                <Typography variant="body1">
                                    {selectedRowData.Remark || 'No remark provided'}
                                </Typography>
                            </Box>

                            <Box sx={{ mt: 1 }}>
                                <Typography variant="subtitle1" sx={{ mb: 0.5, color: '#635bff', fontWeight: 'bold' }}>
                                    Associated Tasks List
                                </Typography>

                                {loadingDetails ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                                        <CircularProgress size={20} />
                                    </Box>
                                ) : apiError ? (
                                    <Alert severity="error" sx={{ py: 0.5, fontSize: '0.875rem' }}>
                                        {apiError}
                                    </Alert>
                                ) : yojanaEntities.length > 0 ? (
                                    <Box sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 0.5
                                    }}>
                                        {yojanaEntities.map((entity) => (
                                            <Box
                                                key={entity.YojanaDtlId}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    px: 0.5
                                                }}
                                            >
                                                <Checkbox
                                                    checked={true}
                                                    disabled
                                                    size="small"
                                                    sx={{
                                                        color: '#635bff',
                                                        padding: 0,
                                                        '&.Mui-checked': {
                                                            color: '#635bff',
                                                        },
                                                    }}
                                                />
                                                <Typography variant="body2" sx={{ ml: 0.5 }}>
                                                    {entity.HkTaskName}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                        No tasks associated
                                    </Typography>
                                )}

                                {yojanaEntities.length > 0 && (
                                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                                        {yojanaEntities.length} task{yojanaEntities.length !== 1 ? 's' : ''}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>

                    <Button
                        variant="contained"
                        onClick={() => {
                            if (selectedRowData?.YojanaId) {
                                navigate('/yojana-mst', {
                                    state: {
                                        YojanaId: selectedRowData.YojanaId,
                                        mode: 'view'
                                    }
                                });
                                handleClosePopup();
                            }
                        }}
                        sx={{
                            backgroundColor: '#635bff',
                            '&:hover': { backgroundColor: '#524ccc' }
                        }}
                    >
                        View Full Details
                    </Button>
                    <Button
                        onClick={handleClosePopup}
                        sx={{ color: '#635bff' }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>


        </Box>
    );
}